import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Check, AlertTriangle, FileText } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ZONE_STYLES = {
  zone_1:     { bg: 'rgba(31,157,122,0.1)',  border: 'rgba(31,157,122,0.3)',  text: '#1F9D7A' },
  zone_2:     { bg: 'rgba(197,139,25,0.1)',  border: 'rgba(197,139,25,0.3)',  text: '#7A5200' },
  zone_3:     { bg: 'rgba(212,175,55,0.1)',  border: 'rgba(212,175,55,0.3)',  text: '#7A5E00' },
  zone_devis: { bg: 'rgba(217,75,93,0.08)', border: 'rgba(217,75,93,0.2)',   text: '#D94B5D' },
  hors_zone:  { bg: 'rgba(107,114,128,0.08)',border: 'rgba(107,114,128,0.2)', text: '#6B7280' },
};

// Messages humains selon zone
function getZoneMessage(zone_id, drive_display) {
  switch (zone_id) {
    case 'zone_1': return { icon: '🎁', label: 'Livraison offerte !', sub: 'Vous êtes dans les 20 km autour de Figeac' };
    case 'zone_2': return { icon: '✅', label: 'Intervention possible avec frais supplémentaires', sub: `Zone 20–45 km — ${drive_display} de Figeac` };
    case 'zone_3': return { icon: '✅', label: 'Intervention possible avec frais supplémentaires', sub: `Au-delà de 45 km — ${drive_display} de Figeac` };
    case 'zone_devis': return { icon: '📋', label: 'Intervention sur devis uniquement', sub: `Distance importante — ${drive_display} de Figeac` };
    default: return { icon: '📋', label: 'Intervention sur devis', sub: 'Hors zone standard — contactez-nous' };
  }
}

export default function ZoneChecker({ onZoneDetected, compact = false, showSeoPhrase = false }) {
  const [address, setAddress] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  async function checkZone() {
    if (!address.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND}/api/livraison/check-address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: address.trim() })
      });
      const data = await res.json();
      setResult(data);
      if (onZoneDetected) onZoneDetected(data);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }

  const zoneStyle = result?.zone ? ZONE_STYLES[result.zone.zone_id] || ZONE_STYLES.zone_1 : null;
  const zoneMsg = result?.zone ? getZoneMessage(result.zone.zone_id, result.drive_display) : null;

  return (
    <div>
      {/* SEO phrase optionnelle */}
      {showSeoPhrase && (
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
          <MapPin size={11} className="inline mr-1" style={{ color: 'var(--teal)' }} />
          Nous intervenons dans un rayon de <strong>1h30 autour de Figeac</strong> — Lot (46), Aveyron, Cantal, Corrèze.
        </p>
      )}

      {!compact && (
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--ink)' }}>
          Vérifier la livraison dans votre ville
        </p>
      )}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={address}
            onChange={e => setAddress(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && checkZone()}
            placeholder="Ville ou code postal (ex: Cahors, 46000)"
            className="w-full pl-9 pr-3 py-2.5 rounded-xl text-sm"
            style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
          />
        </div>
        <button onClick={checkZone} disabled={loading || !address.trim()}
          className="btn-teal px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40">
          {loading ? '…' : 'Vérifier'}
        </button>
      </div>

      {result && (
        <div className="mt-3">
          {/* ── VILLE NON RECONNUE ── */}
          {!result.known ? (
            <div className="p-3 rounded-xl" style={{ background: 'rgba(197,139,25,0.06)', border: '1px solid rgba(197,139,25,0.25)' }}>
              <div className="flex items-start gap-2 mb-2">
                <AlertTriangle size={14} style={{ color: '#C58B19', flexShrink: 0 }} />
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#C58B19' }}>
                    Adresse non reconnue automatiquement
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(197,139,25,0.8)' }}>
                    {result.message}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'rgba(197,139,25,0.6)' }}>
                    {result.suggestion}
                  </p>
                </div>
              </div>
              <Link to="/contact"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold"
                style={{ background: 'rgba(197,139,25,0.15)', color: '#C58B19', border: '1px solid rgba(197,139,25,0.3)' }}>
                <FileText size={13} /> Demander un devis personnalisé
              </Link>
            </div>
          ) : (
            /* ── ZONE RECONNUE ── */
            <div className="p-3 rounded-xl" style={{ background: zoneStyle?.bg, border: `1px solid ${zoneStyle?.border}` }}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-base flex-shrink-0">{zoneMsg?.icon}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: zoneStyle?.text }}>
                      {zoneMsg?.label}
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: `${zoneStyle?.text}99` }}>
                      {zoneMsg?.sub}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs flex-shrink-0 ml-2"
                  style={{ color: zoneStyle?.text }}>
                  <Clock size={11} />
                  {result.drive_display}
                </div>
              </div>

              {/* Tarif transport */}
              {result.zone.tarif_livraison !== null && result.zone.tarif_livraison !== undefined && (
                result.zone.tarif_livraison === 0 ? (
                  <div className="flex items-center gap-2 mt-2 px-2.5 py-1.5 rounded-lg"
                    style={{ background: 'rgba(31,157,122,0.12)', border: '1px solid rgba(31,157,122,0.25)' }}>
                    <span>🎁</span>
                    <span className="text-xs font-bold" style={{ color: '#1F9D7A' }}>
                      Livraison offerte — vous êtes dans les 20 km autour de Figeac !
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 text-xs mt-1">
                    <span style={{ color: `${zoneStyle?.text}99` }}>
                      Livraison : <strong>+{result.zone.tarif_livraison}€</strong>
                    </span>
                    <span style={{ color: `${zoneStyle?.text}80` }}>
                      +Reprise : {result.zone.tarif_reprise === 0 ? 'Incluse' : `+${result.zone.tarif_reprise}€`}
                    </span>
                  </div>
                )
              )}

              {/* Devis requis */}
              {result.devis_required && (
                <div className="mt-2">
                  <Link to="/contact"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold"
                    style={{ background: `${zoneStyle?.text}15`, color: zoneStyle?.text }}>
                    <FileText size={12} /> Demander un devis pour cette zone
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
